import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Inbox, Upload, RefreshCw, Mail, Scale, Search, FileText, ExternalLink, AlertTriangle, CheckCircle2, Loader2,
  Sparkles, Trash2, Copy, Undo2, Link2, Unlink, ArrowDownLeft, ArrowUpRight, X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/* Finanzen › Rechnungen Ein- & Ausgang (03.10.2026).
   Das CRM ist die Vor-Buchhaltung: Eingang (Upload/Postfach → KI liest → prüfen →
   Inhaber gibt frei → Beleg nach Lexware → Bank-Abgleich setzt bezahlt) und Ausgang
   (Spiegel aller Lexware-Ausgangsrechnungen). Rechnungen entstehen weiter in Lexware. */

const euro = (v) => (v == null ? '—' : Number(v).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }));
const datum = (s) => (s ? new Date(`${s.slice(0, 10)}T12:00`).toLocaleDateString('de-DE') : '—');
const heute = () => new Date().toISOString().slice(0, 10);
const STATUS = {
  neu: ['Neu', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'],
  in_pruefung: ['In Prüfung', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
  freigegeben: ['Freigegeben', 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'],
  gebucht: ['Gebucht', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'],
  bezahlt: ['Bezahlt', 'bg-emerald-600 text-white'],
  duplikat: ['Duplikat', 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200'],
  abgelehnt: ['Abgelehnt', 'bg-muted text-muted-foreground line-through'],
};
const AUSGANG_STATUS = {
  open: ['offen', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
  overdue: ['überfällig', 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200'],
  paid: ['bezahlt', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'],
  paidoff: ['bezahlt', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'],
  voided: ['storniert', 'bg-muted text-muted-foreground line-through'],
  draft: ['Entwurf', 'bg-muted text-muted-foreground'],
};
const FILTER_EINGANG = [['', 'Alle'], ['offen', 'Offen'], ['neu', 'Neu'], ['in_pruefung', 'In Prüfung'], ['freigegeben', 'Freigegeben'],
  ['gebucht', 'Gebucht'], ['bezahlt', 'Bezahlt'], ['duplikat', 'Duplikate'], ['abgelehnt', 'Abgelehnt']];
const FILTER_AUSGANG = [['', 'Alle'], ['offen', 'Offen'], ['overdue', 'Überfällig'], ['paid', 'Bezahlt'], ['voided', 'Storniert']];
const QUELLE = { upload: 'Upload', email: 'Postfach', app: 'App' };
const fehlerText = (e, std) => e?.response?.data?.detail || std;

function Badge({ map, k }) {
  const [lab, cls] = map[k] || [k, 'bg-muted'];
  return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${cls}`}>{lab}</span>;
}
function Kachel({ label, wert, unter, warn, onClick, aktiv }) {
  return (
    <Card className={`${onClick ? 'cursor-pointer hover:border-foreground/30' : ''} ${aktiv ? 'border-marke' : ''}`} onClick={onClick}>
      <CardContent className="p-3 md:p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`text-2xl font-extrabold tabular-nums ${warn ? 'text-red-600' : ''}`}>{wert}</div>
        {unter && <div className="text-[11px] text-muted-foreground">{unter}</div>}
      </CardContent>
    </Card>
  );
}
function Chips({ liste, wert, setWert }) {
  return (
    <div className="flex flex-wrap gap-1">
      {liste.map(([k, l]) => (
        <button key={k} onClick={() => setWert(k)} className={`rounded-full px-3 py-1 text-xs border ${wert === k ? 'bg-foreground text-background border-foreground' : 'border-border hover:bg-muted'}`}>{l}</button>
      ))}
    </div>
  );
}
function Hinweis({ kinder, farbe = 'amber' }) {
  const cls = farbe === 'amber' ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200' : 'border-border bg-muted/30 text-muted-foreground';
  return <div className={`flex gap-2 rounded-lg border p-3 text-sm ${cls}`}><AlertTriangle className="w-4 h-4 mt-0.5 flex-none" /><span>{kinder}</span></div>;
}

export default function FinanzenRechnungenPage() {
  const [tab, setTab] = useState('eingang');
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get('/finanzen/rechnungen/status').then(r => setStatus(r.data)).catch(() => setStatus({})); }, []);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="finanzen-rechnungen-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Inbox className="w-6 h-6 text-marke" /> Rechnungen</h1>
          <p className="text-sm text-muted-foreground">Eingang prüfen und nach Lexware geben, Ausgang aus Lexware im Blick. Rechnungen entstehen weiter in Lexware.</p>
        </div>
        <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40">
          {[['eingang', ArrowDownLeft, 'Eingang'], ['ausgang', ArrowUpRight, 'Ausgang']].map(([k, Icon, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`fr-tab-${k}`}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${tab === k ? 'bg-background shadow text-foreground' : 'text-muted-foreground'}`}>
              <Icon className="w-4 h-4" />{l}
            </button>
          ))}
        </div>
      </div>
      {tab === 'eingang' ? <Eingang status={status} /> : <Ausgang status={status} />}
    </div>
  );
}

/* ───────────────────────────── Eingang ───────────────────────────── */
function Eingang({ status }) {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState('offen');
  const [suche, setSuche] = useState('');
  const [busy, setBusy] = useState('');
  const [offenId, setOffenId] = useState(null);
  const [abbuchungen, setAbbuchungen] = useState(null);
  const [drag, setDrag] = useState(false);
  const dateiRef = useRef(null);

  const laden = useCallback(async () => {
    try { setD((await api.get('/finanzen/rechnungen/eingang', { params: { status: filter || undefined, suche: suche || undefined } })).data); }
    catch (e) { toast.error(fehlerText(e, 'Nicht geladen')); }
  }, [filter, suche]);
  useEffect(() => { const t = setTimeout(laden, suche ? 300 : 0); return () => clearTimeout(t); }, [laden, suche]);
  useEffect(() => {                                   // KI läuft im Hintergrund → nachladen, bis nichts mehr aussteht
    if (!d?.uebersicht?.ki_ausstehend && !d?.rechnungen?.some(r => r.ki?.status === 'laeuft')) return;
    const t = setTimeout(laden, 4000);
    return () => clearTimeout(t);
  }, [d, laden]);

  const hochladen = async (dateien) => {
    if (!dateien.length) return;
    setBusy('upload');
    try {
      const fd = new FormData(); dateien.forEach(f => fd.append('dateien', f));
      const r = (await api.post('/finanzen/rechnungen/eingang', fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
      const teile = [`${r.angelegt.length} neu`];
      if (r.schon_da.length) teile.push(`${r.schon_da.length} schon vorhanden`);
      if (r.fehler.length) teile.push(`${r.fehler.length} abgelehnt`);
      toast.success(teile.join(' · '), { duration: 6000 });
      if (r.fehler.length) toast.error(r.fehler.map(f => `${f.datei}: ${f.fehler}`).join(' | '), { duration: 10000 });
      setFilter(''); laden();
      if (r.angelegt.length === 1) setOffenId(r.angelegt[0].id);
    } catch (e) { toast.error(fehlerText(e, 'Upload fehlgeschlagen')); }
    setBusy('');
  };
  const postfach = async () => {
    setBusy('postfach');
    try {
      const r = (await api.post('/finanzen/rechnungen/eingang/postfach/abrufen')).data;
      if (!r.aktiv) toast.info('Postfach-Abruf ist nicht eingerichtet (IMAP unter Einstellungen → E-Mail).');
      else if (r.fehler) toast.error(r.fehler);
      else toast.success(`${r.geprueft} Mails geprüft · ${r.neu} neue Rechnungen · ${r.schon_da} schon da`);
      laden();
    } catch (e) { toast.error(fehlerText(e, 'Abruf fehlgeschlagen')); }
    setBusy('');
  };
  const abgleich = async () => {
    setBusy('abgleich');
    try {
      const r = (await api.post('/finanzen/rechnungen/eingang/abgleich')).data;
      toast.success(`${r.geprueft} Rechnungen geprüft · ${r.zugeordnet} als bezahlt erkannt${r.mehrdeutig ? ` · ${r.mehrdeutig} mehrdeutig (bitte manuell)` : ''}`);
      laden(); if (abbuchungen) ladeAbbuchungen();
    } catch (e) { toast.error(fehlerText(e, 'Abgleich fehlgeschlagen')); }
    setBusy('');
  };
  const ladeAbbuchungen = async () => {
    try { setAbbuchungen((await api.get('/finanzen/rechnungen/eingang/abbuchungen')).data.abbuchungen); } catch { setAbbuchungen([]); }
  };
  // stabil, sonst laedt die Pruefmaske bei jedem Poll der Liste neu und verwirft Eingaben
  const schliessen = useCallback(() => { setOffenId(null); laden(); }, [laden]);

  const u = d?.uebersicht;
  const rechnungen = d?.rechnungen || [];

  return (
    <div className="space-y-4"
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); hochladen(Array.from(e.dataTransfer.files || [])); }}>
      {status && !status.ki && <Hinweis kinder={<>Die KI liest Rechnungen erst, wenn in Coolify <b>KI_RECHNUNGEN=1</b> (nur dieses Modul) und der Anthropic-Schlüssel gesetzt sind. Bis dahin: Felder in der Prüfmaske von Hand eintragen.</>} />}
      {status && !status.lexware && <Hinweis kinder={<>Lexware ist nicht verbunden — freigegebene Rechnungen bleiben auf „Freigegeben“ und werden nach dem Setzen von <b>LEXWARE_API_KEY</b> mit einem Klick nachgeholt.</>} />}

      {u && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Kachel label="Neu" wert={u.je_status.neu.anzahl} unter="eingegangen, ungeprüft" onClick={() => setFilter('neu')} aktiv={filter === 'neu'} />
          <Kachel label="In Prüfung" wert={u.je_status.in_pruefung.anzahl} unter={euro(u.je_status.in_pruefung.summe)} onClick={() => setFilter('in_pruefung')} aktiv={filter === 'in_pruefung'} />
          <Kachel label="Gebucht, offen" wert={u.je_status.gebucht.anzahl + u.je_status.freigegeben.anzahl} unter={euro(u.je_status.gebucht.summe + u.je_status.freigegeben.summe)} onClick={() => setFilter('gebucht')} aktiv={filter === 'gebucht'} />
          <Kachel label="Überfällig" wert={u.ueberfaellig} unter={`${u.faellig_7_tage} fällig in 7 Tagen`} warn={u.ueberfaellig > 0} />
          <Kachel label="Bezahlt (Monat)" wert={u.bezahlt_monat.anzahl} unter={euro(u.bezahlt_monat.summe)} onClick={() => setFilter('bezahlt')} aktiv={filter === 'bezahlt'} />
          <Kachel label="Duplikate" wert={u.je_status.duplikat.anzahl} unter="zur Kontrolle" warn={u.je_status.duplikat.anzahl > 0} onClick={() => setFilter('duplikat')} aktiv={filter === 'duplikat'} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input ref={dateiRef} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={(e) => { hochladen(Array.from(e.target.files || [])); e.target.value = ''; }} />
        <Button size="sm" className="bg-marke hover:bg-marke-dunkel" disabled={busy === 'upload'} onClick={() => dateiRef.current?.click()} data-testid="fr-upload">
          {busy === 'upload' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Rechnungen hochladen
        </Button>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={postfach} title={status?.postfach ? `Postfach ${status.postfach_adresse}, letzte ${status.postfach_tage} Tage` : 'IMAP nicht eingerichtet'}>
          {busy === 'postfach' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />} Postfach abrufen
        </Button>
        <Button size="sm" variant="outline" disabled={!!busy} onClick={abgleich}>
          {busy === 'abgleich' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Scale className="w-4 h-4 mr-1" />} Mit Konto abgleichen
        </Button>
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
          <Input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Lieferant, Nummer, Datei …" className="pl-8 h-9 w-56" />
        </div>
      </div>
      <Chips liste={FILTER_EINGANG} wert={filter} setWert={setFilter} />

      <Card className={drag ? 'border-dashed border-2 border-marke' : ''}><CardContent className="p-0 overflow-x-auto">
        {!rechnungen.length ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Upload className="w-8 h-8 mx-auto mb-2 opacity-40" />
            {filter || suche ? 'Nichts in dieser Auswahl.' : 'Noch keine Eingangsrechnungen. PDFs hierher ziehen oder oben hochladen — oder das Postfach abrufen.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground"><tr>
              <th className="px-3 py-2 text-left font-semibold">Lieferant</th><th className="px-3 py-2 text-left font-semibold">Rechnungs-Nr.</th>
              <th className="px-3 py-2 text-left font-semibold">Datum</th><th className="px-3 py-2 text-right font-semibold">Betrag</th>
              <th className="px-3 py-2 text-right font-semibold">USt.</th><th className="px-3 py-2 text-left font-semibold">Fälligkeit</th>
              <th className="px-3 py-2 text-left font-semibold">Kategorie</th><th className="px-3 py-2 text-left font-semibold">Status</th><th className="px-3 py-2 text-left font-semibold">Quelle</th>
            </tr></thead>
            <tbody>
              {rechnungen.map(r => {
                const ueberfaellig = r.faellig_am && r.faellig_am < heute() && !['bezahlt', 'abgelehnt', 'duplikat'].includes(r.status);
                return (
                  <tr key={r.id} className="border-b border-border/40 last:border-0 cursor-pointer hover:bg-muted/30" onClick={() => setOffenId(r.id)} data-testid={`fr-zeile-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium flex items-center gap-1.5">
                        {r.ki?.status === 'laeuft' || r.ki?.status === 'ausstehend' ? <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /> : null}
                        {r.lieferant || <span className="text-muted-foreground italic">{r.datei?.name}</span>}
                      </div>
                      {(r.hinweise || []).length > 0 && <div className="text-[11px] text-amber-700 dark:text-amber-400 flex gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 flex-none" />{r.hinweise[0]}</div>}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.rechnungsnummer || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{datum(r.rechnungsdatum)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold whitespace-nowrap">{euro(r.betrag_brutto)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{r.ust_satz != null ? `${r.ust_satz} %` : '—'}</td>
                    <td className={`px-3 py-2 whitespace-nowrap ${ueberfaellig ? 'text-red-600 font-medium' : ''}`}>{datum(r.faellig_am)}{ueberfaellig && <span className="text-[10px] ml-1">(überfällig)</span>}</td>
                    <td className="px-3 py-2 text-xs">{status?.kategorien?.find(k => k.key === r.kategorie)?.label || '—'}</td>
                    <td className="px-3 py-2"><Badge map={STATUS} k={r.status} />{r.zahlung && <div className="text-[10px] text-muted-foreground mt-0.5">{datum(r.zahlung.buchungstag)}{r.zahlung.art === 'auto' ? ' · automatisch' : ''}</div>}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{QUELLE[r.quelle] || r.quelle}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent></Card>

      <details className="rounded-lg border border-border" onToggle={(e) => e.target.open && !abbuchungen && ladeAbbuchungen()}>
        <summary className="cursor-pointer px-3 py-2 text-sm font-medium flex items-center gap-2"><Scale className="w-4 h-4" /> Abbuchungen ohne Beleg {abbuchungen && <span className="text-xs text-muted-foreground font-normal">({abbuchungen.length})</span>}</summary>
        <div className="px-3 pb-3 text-xs text-muted-foreground">Abgänge vom Geschäftskonto (Sparkassen-Import unter Rechnungslauf → Soll-Ist), denen noch keine Eingangsrechnung zugeordnet ist — die Liste „welcher Beleg fehlt noch?“.</div>
        {abbuchungen && abbuchungen.length > 0 && (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground"><tr><th className="px-3 py-2 text-left">Buchungstag</th><th className="px-3 py-2 text-left">Name</th><th className="px-3 py-2 text-left">Verwendungszweck</th><th className="px-3 py-2 text-right">Betrag</th></tr></thead>
            <tbody>{abbuchungen.slice(0, 100).map(a => (
              <tr key={a.id} className="border-t border-border/40"><td className="px-3 py-1.5 whitespace-nowrap">{datum(a.buchungstag)}</td><td className="px-3 py-1.5">{a.name}</td><td className="px-3 py-1.5 text-xs text-muted-foreground truncate max-w-md">{a.verwendungszweck}</td><td className="px-3 py-1.5 text-right tabular-nums text-red-600">{euro(a.betrag)}</td></tr>
            ))}</tbody>
          </table></div>
        )}
      </details>

      {offenId && <EingangDetail id={offenId} status={status} onClose={schliessen} />}
    </div>
  );
}

/* ─────────────────────────── Prüfmaske ─────────────────────────── */
function EingangDetail({ id, status, onClose }) {
  const [r, setR] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState('');
  const [vorschlaege, setVorschlaege] = useState(null);
  const [frage, setFrage] = useState(null);           // 'freigeben' | 'ablehnen' | 'loeschen'
  const [grund, setGrund] = useState('');
  const dirty = useRef(false);                        // ungespeicherte Eingaben: der KI-Poll darf sie nicht ueberschreiben
  const { user } = useAuth();
  const istInhaber = user?.role === 'admin';

  const laden = useCallback(async () => {
    try {
      const x = (await api.get(`/finanzen/rechnungen/eingang/${id}`)).data;
      setR(x);
      if (dirty.current) return;
      setF({ lieferant: x.lieferant || '', rechnungsnummer: x.rechnungsnummer || '', rechnungsdatum: x.rechnungsdatum || '', faellig_am: x.faellig_am || '',
        betrag_netto: x.betrag_netto ?? '', ust_satz: x.ust_satz ?? '', ust_betrag: x.ust_betrag ?? '', betrag_brutto: x.betrag_brutto ?? '',
        kategorie: x.kategorie || '', beschreibung: x.beschreibung || '', notiz: x.notiz || '', iban: '' });
    } catch (e) { toast.error(fehlerText(e, 'Nicht geladen')); onClose(); }
  }, [id, onClose]);
  useEffect(() => { laden(); }, [laden]);
  useEffect(() => {
    if (!r || !['laeuft', 'ausstehend'].includes(r.ki?.status)) return;
    const t = setTimeout(laden, 3000); return () => clearTimeout(t);
  }, [r, laden]);

  const setze = (k, v) => { dirty.current = true; setF(prev => ({ ...prev, [k]: v })); };
  const aktion = async (fn, ok) => {
    setBusy('aktion');
    let gut = true;
    try { const x = await fn(); if (x?.data) setR(x.data); if (ok) toast.success(ok); dirty.current = false; await laden(); }
    catch (e) { toast.error(fehlerText(e, 'Fehlgeschlagen')); gut = false; }
    setBusy(''); setFrage(null);
    return gut;
  };
  const speichern = () => {
    const body = { ...f };
    Object.keys(body).forEach(k => { if (body[k] === '') body[k] = null; });
    if (!body.iban) delete body.iban;
    return aktion(() => api.put(`/finanzen/rechnungen/eingang/${id}`, body), 'Gespeichert');
  };
  const freigeben = async () => {
    if (!(await speichern())) return;                 // ungueltige Eingabe → nichts freigeben
    await aktion(async () => {
      const x = await api.post(`/finanzen/rechnungen/eingang/${id}/freigeben`);
      if (x.data.status === 'gebucht') toast.success('Beleg liegt in Lexware (Belegeingang)');
      else if (x.data.lexware?.fehler) toast.warning(`Freigegeben, aber Lexware-Upload offen: ${x.data.lexware.fehler}`, { duration: 8000 });
      return x;
    });
  };
  const ladeVorschlaege = async () => {
    try { setVorschlaege((await api.get(`/finanzen/rechnungen/eingang/${id}/zahlung/vorschlaege`)).data.vorschlaege); } catch { setVorschlaege([]); }
  };

  if (!r) return null;
  const gesperrt = ['freigegeben', 'gebucht', 'bezahlt'].includes(r.status);
  const aussortiert = ['abgelehnt', 'duplikat'].includes(r.status);
  const kiFelder = r.ki?.felder || {};
  // Render-Funktion statt innerer Komponente: eine innere Komponente würde bei jedem
  // Tastendruck neu gemountet und das Feld verlöre den Fokus.
  const feld = (k, label, typ = 'text', breit = false) => (
    <label key={k} className={`block ${breit ? 'md:col-span-2' : ''}`}>
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}{kiFelder[k] != null && kiFelder[k] !== '' && String(kiFelder[k]) === String(f[k]) && <Sparkles className="w-3 h-3 inline ml-1 text-violet-500" title="von der KI gelesen" />}</span>
      <Input type={typ} value={f[k] ?? ''} disabled={gesperrt && !['kategorie', 'beschreibung', 'notiz'].includes(k)} onChange={e => setze(k, e.target.value)} className="h-9 mt-0.5" />
    </label>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[92vh] overflow-y-auto p-0">
        <div className="grid md:grid-cols-[1.1fr_1fr] min-h-[70vh]">
          <div className="bg-muted/40 border-r border-border flex flex-col">
            <div className="px-4 py-3 flex items-center justify-between text-xs text-muted-foreground">
              <span className="truncate">{r.datei?.name} · {Math.round((r.datei?.groesse || 0) / 1024)} KB · {QUELLE[r.quelle]}{r.email?.absender ? ` · ${r.email.absender}` : ''}</span>
              <a className="underline inline-flex items-center gap-1 flex-none" href={`${process.env.REACT_APP_BACKEND_URL}${r.datei_url}`} target="_blank" rel="noreferrer" onClick={async (e) => {
                e.preventDefault();
                try { const x = await api.get(r.datei_url.replace('/api', ''), { responseType: 'blob' }); window.open(URL.createObjectURL(x.data), '_blank'); } catch { toast.error('Datei nicht abrufbar'); }
              }}><FileText className="w-3 h-3" /> öffnen</a>
            </div>
            <Vorschau url={r.datei_url} mimetype={r.datei?.mimetype} />
          </div>

          <div className="p-4 md:p-5 space-y-4">
            <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2">
              {r.lieferant || 'Eingangsrechnung'} <Badge map={STATUS} k={r.status} />
              {r.ki?.status === 'laeuft' || r.ki?.status === 'ausstehend' ? <span className="text-xs font-normal text-muted-foreground inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> KI liest …</span> : null}
              {r.ki?.status === 'fertig' && <span className="text-xs font-normal text-violet-600 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> KI {Math.round((r.ki.konfidenz || 0) * 100)} % sicher</span>}
              {r.ki?.status === 'fehler' && <span className="text-xs font-normal text-red-600">KI: {r.ki.fehler}</span>}
            </DialogTitle></DialogHeader>

            {r.status === 'duplikat' && r.duplikat_original && (
              <Hinweis kinder={<>Duplikat von <b>{r.duplikat_original.lieferant} {r.duplikat_original.rechnungsnummer}</b> ({euro(r.duplikat_original.betrag_brutto)}, Status {STATUS[r.duplikat_original.status]?.[0]}). Falsch erkannt? Unten „wieder öffnen“.</>} />
            )}
            {(r.hinweise || []).map((h, i) => <Hinweis key={i} kinder={h} />)}
            {r.status === 'freigegeben' && r.lexware?.fehler && <Hinweis kinder={<>Lexware-Upload offen: {r.lexware.fehler}</>} />}
            {r.lexware?.voucher_id && <p className="text-xs text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> In Lexware als Beleg {r.lexware.voucher_id} (OCR dort, Kontierung durch Steuerberater)</p>}

            <div className="grid md:grid-cols-2 gap-3">
              {feld('lieferant', 'Lieferant')}
              {feld('rechnungsnummer', 'Rechnungsnummer')}
              {feld('rechnungsdatum', 'Rechnungsdatum', 'date')}
              {feld('faellig_am', 'Fällig am', 'date')}
              {feld('betrag_netto', 'Netto (€)')}
              <label className="block"><span className="text-[11px] uppercase tracking-wide text-muted-foreground">USt-Satz</span>
                <select value={f.ust_satz ?? ''} disabled={gesperrt} onChange={e => setze('ust_satz', e.target.value)} className="mt-0.5 h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
                  <option value="">—</option>{(status?.ust_saetze || [0, 7, 19]).map(s => <option key={s} value={s}>{s} %</option>)}
                </select></label>
              {feld('ust_betrag', 'USt (€)')}
              {feld('betrag_brutto', 'Brutto / Zahlbetrag (€)')}
              <label className="block"><span className="text-[11px] uppercase tracking-wide text-muted-foreground">Kategorie</span>
                <select value={f.kategorie || ''} onChange={e => setze('kategorie', e.target.value)} className="mt-0.5 h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
                  <option value="">—</option>{(status?.kategorien || []).map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select></label>
              <label className="block"><span className="text-[11px] uppercase tracking-wide text-muted-foreground">IBAN Lieferant {r.iban_maskiert && <span className="normal-case font-mono">({r.iban_maskiert})</span>}</span>
                <Input value={f.iban} disabled={gesperrt} placeholder={r.iban_maskiert ? 'hinterlegt — hier ändern' : 'für den Bank-Abgleich'} onChange={e => setze('iban', e.target.value)} className="h-9 mt-0.5 font-mono" /></label>
              {feld('beschreibung', 'Was wurde berechnet?', 'text', true)}
              <label className="block md:col-span-2"><span className="text-[11px] uppercase tracking-wide text-muted-foreground">Notiz (intern)</span>
                <Textarea value={f.notiz} onChange={e => setze('notiz', e.target.value)} rows={2} className="mt-0.5" /></label>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {!aussortiert && <Button size="sm" variant="outline" disabled={!!busy} onClick={speichern}>Speichern</Button>}
              {!gesperrt && !aussortiert && istInhaber && <Button size="sm" className="bg-marke hover:bg-marke-dunkel" disabled={!!busy} onClick={() => setFrage('freigeben')} data-testid="fr-freigeben">Freigeben → Lexware</Button>}
              {r.status === 'freigegeben' && istInhaber && <Button size="sm" className="bg-marke hover:bg-marke-dunkel" disabled={!!busy} onClick={() => aktion(() => api.post(`/finanzen/rechnungen/eingang/${id}/freigeben`), 'Erneut versucht')}><RefreshCw className="w-4 h-4 mr-1" /> Lexware-Upload nachholen</Button>}
              {!gesperrt && !aussortiert && status?.ki && r.ki?.status !== 'laeuft' && <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => aktion(() => api.post(`/finanzen/rechnungen/eingang/${id}/ki`), 'KI hat gelesen')}><Sparkles className="w-4 h-4 mr-1" /> KI erneut lesen</Button>}
              {!gesperrt && !aussortiert && istInhaber && <Button size="sm" variant="ghost" className="text-muted-foreground" disabled={!!busy} onClick={() => setFrage('ablehnen')}><X className="w-4 h-4 mr-1" /> Ablehnen</Button>}
              {aussortiert && istInhaber && <Button size="sm" variant="outline" disabled={!!busy} onClick={() => aktion(() => api.post(`/finanzen/rechnungen/eingang/${id}/wiederoeffnen`), 'Wieder in Prüfung')}><Undo2 className="w-4 h-4 mr-1" /> Wieder öffnen</Button>}
              {['neu', 'abgelehnt', 'duplikat'].includes(r.status) && istInhaber && <Button size="sm" variant="ghost" className="text-red-600" disabled={!!busy} onClick={() => setFrage('loeschen')}><Trash2 className="w-4 h-4 mr-1" /> Löschen</Button>}
            </div>

            {/* Zahlung */}
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <b className="text-sm inline-flex items-center gap-1"><Scale className="w-4 h-4" /> Zahlung</b>
                {r.status !== 'bezahlt' && !aussortiert && <Button size="sm" variant="outline" className="h-7" onClick={ladeVorschlaege}>Abbuchung suchen</Button>}
              </div>
              {r.status === 'bezahlt' && r.zahlung ? (
                <div className="text-sm flex items-start justify-between gap-2">
                  <div><CheckCircle2 className="w-4 h-4 inline text-emerald-600 mr-1" />{datum(r.zahlung.buchungstag)} · {euro(r.zahlung.betrag)} · {r.zahlung.name}
                    <div className="text-xs text-muted-foreground">{r.zahlung.verwendungszweck} · {r.zahlung.art === 'auto' ? `automatisch (${Math.round(r.zahlung.konfidenz * 100)} %)` : 'manuell zugeordnet'}</div></div>
                  <button className="text-xs underline text-muted-foreground inline-flex items-center gap-1 flex-none" onClick={() => aktion(() => api.delete(`/finanzen/rechnungen/eingang/${id}/zahlung`), 'Zuordnung gelöst')}><Unlink className="w-3 h-3" /> lösen</button>
                </div>
              ) : vorschlaege === null ? (
                <p className="text-xs text-muted-foreground">Noch nicht bezahlt. Der Abgleich läuft automatisch nach jedem Kontoauszug-Import; hier lässt sich eine Abbuchung auch von Hand zuordnen.</p>
              ) : !vorschlaege.length ? (
                <p className="text-xs text-muted-foreground">Keine passende Abbuchung gefunden (gleicher Betrag, Zeitraum ab Rechnungsdatum). Kontoauszug schon importiert?</p>
              ) : (
                <ul className="space-y-1">{vorschlaege.map(v => (
                  <li key={v.id} className="flex items-center justify-between gap-2 text-sm rounded-md border border-border/60 px-2 py-1.5">
                    <div><span className="tabular-nums">{datum(v.buchungstag)}</span> · <b>{euro(v.betrag)}</b> · {v.name}<div className="text-[11px] text-muted-foreground">{v.verwendungszweck} · {v.gruende.join(', ')} · {Math.round(v.konfidenz * 100)} %</div></div>
                    <Button size="sm" className="h-7 flex-none" variant={v.konfidenz >= 0.8 ? 'default' : 'outline'} disabled={!!busy} onClick={() => aktion(() => api.post(`/finanzen/rechnungen/eingang/${id}/zahlung`, { umsatz_id: v.id }), 'Als bezahlt markiert')}><Link2 className="w-3 h-3 mr-1" /> zuordnen</Button>
                  </li>))}</ul>
              )}
            </div>

            <details className="text-xs text-muted-foreground"><summary className="cursor-pointer">Verlauf ({(r.historie || []).length})</summary>
              <ul className="mt-1 space-y-0.5">{(r.historie || []).slice().reverse().map((h, i) => <li key={i}>{new Date(h.am).toLocaleString('de-DE')} · {h.aktion} · {h.von}{h.grund ? ` · ${h.grund}` : ''}</li>)}</ul>
            </details>
          </div>
        </div>

        <Dialog open={!!frage} onOpenChange={(o) => !o && setFrage(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{frage === 'freigeben' ? 'Freigeben und nach Lexware geben?' : frage === 'ablehnen' ? 'Rechnung ablehnen?' : 'Datensatz löschen?'}</DialogTitle></DialogHeader>
            {frage === 'freigeben' && <p className="text-sm">{f.lieferant || 'Lieferant'} · {euro(f.betrag_brutto)} · {datum(f.rechnungsdatum)}. Der Beleg wird in den Lexware-Belegeingang hochgeladen; Kontierung und Buchung passieren dort. Danach sind Betrag, Nummer und Datum hier nicht mehr änderbar.</p>}
            {frage === 'ablehnen' && <><p className="text-sm">Kein Beleg für die Buchhaltung (Werbung, Angebot, falscher Empfänger)? Der Datensatz bleibt als Spur erhalten.</p><Input value={grund} onChange={e => setGrund(e.target.value)} placeholder="Grund (optional)" /></>}
            {frage === 'loeschen' && <p className="text-sm">Datei und Datensatz werden endgültig entfernt. Nur für Neu, Abgelehnt und Duplikate möglich.</p>}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setFrage(null)}>Abbrechen</Button>
              {frage === 'freigeben' && <Button className="bg-marke hover:bg-marke-dunkel" onClick={freigeben} data-testid="fr-freigeben-ok">Freigeben</Button>}
              {frage === 'ablehnen' && <Button variant="destructive" onClick={() => aktion(() => api.post(`/finanzen/rechnungen/eingang/${id}/ablehnen`, { grund }), 'Abgelehnt')}>Ablehnen</Button>}
              {frage === 'loeschen' && <Button variant="destructive" onClick={async () => { try { await api.delete(`/finanzen/rechnungen/eingang/${id}`); toast.success('Gelöscht'); onClose(); } catch (e) { toast.error(fehlerText(e, 'Nicht gelöscht')); } }}>Löschen</Button>}
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function Vorschau({ url, mimetype }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let objekt = null;
    api.get(url.replace('/api', ''), { responseType: 'blob' }).then(r => { objekt = URL.createObjectURL(r.data); setSrc(objekt); }).catch(() => setSrc(''));
    return () => { if (objekt) URL.revokeObjectURL(objekt); };
  }, [url]);
  if (src === null) return <div className="flex-1 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!src) return <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Vorschau nicht verfügbar</div>;
  if (mimetype === 'application/pdf') return <iframe title="Rechnung" src={src} className="flex-1 w-full min-h-[60vh]" />;
  return <div className="flex-1 overflow-auto p-2"><img src={src} alt="Rechnung" className="max-w-full mx-auto" /></div>;
}

/* ───────────────────────────── Ausgang ───────────────────────────── */
function Ausgang({ status }) {
  const jahr = new Date().getFullYear();
  const [von, setVon] = useState(`${jahr}-01-01`);
  const [bis, setBis] = useState(heute());
  const [filter, setFilter] = useState('');
  const [suche, setSuche] = useState('');
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState('');

  const laden = useCallback(async (aktualisieren = false) => {
    setBusy(aktualisieren ? 'sync' : 'laden');
    try { setD((await api.get('/finanzen/rechnungen/ausgang', { params: { status: filter || undefined, von, bis, suche: suche || undefined, aktualisieren: aktualisieren || undefined } })).data); }
    catch (e) { toast.error(fehlerText(e, 'Nicht geladen')); }
    setBusy('');
  }, [filter, von, bis, suche]);
  useEffect(() => { const t = setTimeout(() => laden(false), suche ? 300 : 0); return () => clearTimeout(t); }, [laden, suche]);

  const pdf = async (r) => {
    try { const x = await api.get(`/finanzen/rechnungen/ausgang/${r.lexware_id}/pdf`, { responseType: 'blob' }); window.open(URL.createObjectURL(x.data), '_blank'); }
    catch { toast.error('PDF nicht abrufbar (Entwurf oder Lexware nicht verbunden)'); }
  };
  const u = d?.uebersicht;
  const rows = d?.rechnungen || [];
  const stand = u?.stand?.synchronisiert_am ? new Date(u.stand.synchronisiert_am).toLocaleString('de-DE') : null;

  return (
    <div className="space-y-4">
      {status && !status.lexware && <Hinweis kinder={<>Lexware ist nicht verbunden — der Ausgang zeigt den letzten Spiegelstand{stand ? ` (${stand})` : ''}. Schlüssel <b>LEXWARE_API_KEY</b> in Coolify setzen.</>} />}
      {d?.sync?.fehler && <Hinweis kinder={<>Lexware-Abruf fehlgeschlagen: {d.sync.fehler}</>} />}
      {u && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kachel label="Gestellt im Zeitraum" wert={u.gestellt.anzahl} unter={u.umsatz_gesperrt ? 'verborgen' : euro(u.gestellt.betrag)} />
          <Kachel label="Offen" wert={(u.je_status.open?.anzahl || 0) + (u.je_status.overdue?.anzahl || 0)} unter={u.umsatz_gesperrt ? 'verborgen' : euro(u.offen_summe)} onClick={() => setFilter('offen')} aktiv={filter === 'offen'} />
          <Kachel label="Überfällig" wert={u.ueberfaellig.anzahl} unter={u.umsatz_gesperrt ? 'verborgen' : euro(u.ueberfaellig.offen)} warn={u.ueberfaellig.anzahl > 0} onClick={() => setFilter('overdue')} aktiv={filter === 'overdue'} />
          <Kachel label="Bezahlt" wert={(u.je_status.paid?.anzahl || 0) + (u.je_status.paidoff?.anzahl || 0)} unter={u.umsatz_gesperrt ? 'verborgen' : euro((u.je_status.paid?.betrag || 0) + (u.je_status.paidoff?.betrag || 0))} onClick={() => setFilter('paid')} aktiv={filter === 'paid'} />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input type="date" value={von} onChange={e => setVon(e.target.value)} className="h-9 w-40" />
        <span className="text-xs text-muted-foreground">bis</span>
        <Input type="date" value={bis} onChange={e => setBis(e.target.value)} className="h-9 w-40" />
        <Button size="sm" variant="outline" disabled={!!busy || !status?.lexware} onClick={() => laden(true)}>
          {busy === 'sync' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Aus Lexware aktualisieren
        </Button>
        {stand && <span className="text-xs text-muted-foreground">Stand {stand}</span>}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
          <Input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Nummer, Kunde …" className="pl-8 h-9 w-56" />
        </div>
      </div>
      <Chips liste={FILTER_AUSGANG} wert={filter} setWert={setFilter} />
      <Card><CardContent className="p-0 overflow-x-auto">
        {!rows.length ? (
          <p className="p-6 text-sm text-muted-foreground">{status?.lexware ? 'Keine Ausgangsrechnungen in dieser Auswahl.' : 'Noch kein Spiegel aus Lexware — sobald der Schlüssel gesetzt ist, lädt die Seite die Belegliste.'}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground"><tr>
              <th className="px-3 py-2 text-left font-semibold">Nr.</th><th className="px-3 py-2 text-left font-semibold">Datum</th><th className="px-3 py-2 text-left font-semibold">Fällig</th>
              <th className="px-3 py-2 text-left font-semibold">Kunde</th><th className="px-3 py-2 text-right font-semibold">Betrag</th><th className="px-3 py-2 text-right font-semibold">Offen</th>
              <th className="px-3 py-2 text-left font-semibold">Status</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.lexware_id} className="border-b border-border/40 last:border-0">
                <td className="px-3 py-2 font-mono text-xs">{r.nummer || <span className="text-muted-foreground">Entwurf</span>}{r.typ && r.typ !== 'invoice' && <span className="ml-1 text-[10px] text-muted-foreground">{r.typ === 'creditnote' ? 'Gutschrift' : r.typ}</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap">{datum(r.datum)}</td>
                <td className={`px-3 py-2 whitespace-nowrap ${r.status === 'overdue' ? 'text-red-600 font-medium' : ''}`}>{datum(r.faellig_am)}</td>
                <td className="px-3 py-2">{r.zahler_lead_id ? <Link to={`/leads/${r.zahler_lead_id}`} className="underline underline-offset-2">{r.zahler_name || r.kontakt_name}</Link> : r.kontakt_name}
                  {r.quelle === 'rechnungslauf' && <span className="ml-1 text-[10px] text-muted-foreground">Beitrag</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums font-bold">{euro(r.betrag)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${r.offen > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'}`}>{r.offen > 0 ? euro(r.offen) : '—'}</td>
                <td className="px-3 py-2"><Badge map={AUSGANG_STATUS} k={r.status} /></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.nummer && <button className="text-xs underline inline-flex items-center gap-1 mr-2" onClick={() => pdf(r)}><FileText className="w-3 h-3" />PDF</button>}
                  {r.lexware_link && <a className="text-xs underline inline-flex items-center gap-1" href={r.lexware_link} target="_blank" rel="noreferrer"><ExternalLink className="w-3 h-3" />Lexware</a>}
                </td>
              </tr>))}</tbody>
          </table>
        )}
      </CardContent></Card>
      <p className="text-xs text-muted-foreground inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Spiegel der Lexware-Belegliste (Rechnungen, Gutschriften, Abschlagsrechnungen) · Monatsbeiträge entstehen im <Link to="/rechnungen" className="underline">Rechnungslauf</Link>, alles andere in Lexware.</p>
    </div>
  );
}
